import React from 'react';

import {Log} from '../../components/Log';
import {useAutoRefreshSettings} from '../../containers/AutoRefreshSettings';
import {useRunPageLogsQuery} from '../../generated/graphql';
import {useBasePollInterval} from '../../pages/Run/useBasePollInterval';
import {useRampFlagRunLogsPagination} from '../../util/rampFeatureFlags/rampFeatureFlags';
import * as RunHelpers from '../../util/runhelpers';
import NoMatch from '../NoMatch';
import {TabWaveLoader} from './TabWaveLoader';

// The polling rate used for fetching logs when the "run_logs_pagination"
// feature is enabled.
export const POLL_INTERVAL_DEFAULT_MS = 5_000;

type RunPageLogsProps = {
  entityName: string;
  projectName: string;
  runName: string;
};

export const RunPageLogs: React.FC<RunPageLogsProps> = ({
  entityName,
  projectName,
  runName,
}) => {
  const {data: runData, loading: runDataLoading} = useRunPageLogsQuery({
    variables: {
      projectName,
      entityName,
      runName,
    },
  });

  const run = runData?.project?.run;

  const {autoRefreshEnabled} = useAutoRefreshSettings();

  const basePollInterval = useBasePollInterval({
    runState: run?.state ?? undefined,
    runHistoryKeys: run?.historyKeys,
    autoRefreshEnabled,
  });

  const useImprovedPagination = useRampFlagRunLogsPagination();
  const pollInterval = autoRefreshEnabled
    ? useImprovedPagination
      ? POLL_INTERVAL_DEFAULT_MS
      : basePollInterval
    : 0;

  if (runDataLoading) {
    return <TabWaveLoader />;
  }

  if (!run) {
    return <NoMatch />;
  }

  const outputLogFile = RunHelpers.outputLogFile(run?.outputLogFile);
  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <div style={{flex: '1 1 auto'}}>
        <Log
          analyticsLocation="run-logs"
          entityName={entityName}
          outputLogFile={outputLogFile}
          pollInterval={pollInterval}
          projectName={projectName}
          runName={runName}
          totalLines={run?.logLineCount ?? 0}
        />
      </div>
    </div>
  );
};
RunPageLogs.displayName = 'RunPageLogs';
